import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DollarSign,
  Users,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Play,
  Loader2,
  FileText,
  Award,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { payrollApi, employeeApi, timeAttendanceApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';

const Payroll: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch payroll data
  const { data: payrollRuns = [], isLoading: runsLoading } = useQuery({
    queryKey: ['payrollRuns'],
    queryFn: () => payrollApi.runs.getAll(),
  });

  const { data: payrollProfiles = [], isLoading: profilesLoading } = useQuery({
    queryKey: ['payrollProfiles'],
    queryFn: () => payrollApi.profiles.getAll(),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  const { data: benefits = [] } = useQuery({
    queryKey: ['benefits'],
    queryFn: () => payrollApi.benefits.getAll(),
  });

  const { data: deductions = [] } = useQuery({
    queryKey: ['deductions'],
    queryFn: () => payrollApi.deductions.getAll(),
  });

  const { data: timeRecords = [] } = useQuery({
    queryKey: ['timeAttendance'],
    queryFn: () => timeAttendanceApi.attendance.getAll({}),
  });

  const isLoading = runsLoading || profilesLoading;

  // Calculate metrics
  const totalGrossPay = payrollRuns.reduce(
    (sum: number, run: any) => sum + (run.gross_amount || run.total_amount || 0),
    0
  );

  const totalDeductions = payrollRuns.reduce(
    (sum: number, run: any) => sum + (run.deductions || 0),
    0
  );

  const totalNetPay = payrollRuns.reduce(
    (sum: number, run: any) => sum + (run.net_amount || run.total_amount || 0),
    0
  );

  const activeEmployeesCount = employees.filter((e: any) => e.status === 'ACTIVE').length;

  const completedRuns = payrollRuns.filter((r: any) => r.status === 'COMPLETED').length;
  const processingRuns = payrollRuns.filter((r: any) => r.status === 'PROCESSING').length;
  const draftRuns = payrollRuns.filter((r: any) => r.status === 'DRAFT').length;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
        return 'default';
      case 'PROCESSING':
        return 'secondary';
      case 'DRAFT':
        return 'outline';
      case 'FAILED':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find((e: any) => e.id === employeeId);
    return employee
      ? `${employee.first_name} ${employee.last_name || ''}`
      : 'Unknown';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading payroll data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payroll Management</h1>
          <p className="text-gray-600 mt-2">Manage payroll processing, calculations, and employee compensation</p>
        </div>
        <Button
          onClick={() => navigate('/payroll/runs')}
          className="flex items-center space-x-2"
        >
          <Play className="h-4 w-4" />
          <span>Process Payroll</span>
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Gross Pay</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalGrossPay)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across {payrollRuns.length} payroll runs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Net Pay</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalNetPay)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              After deductions: {formatCurrency(totalDeductions)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Employees</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeEmployeesCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {payrollProfiles.length} payroll profiles
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Payroll Status</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedRuns}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {processingRuns} processing, {draftRuns} draft
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="runs">Payroll Runs</TabsTrigger>
          <TabsTrigger value="profiles">Employee Profiles</TabsTrigger>
          <TabsTrigger value="components">Salary Components</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Quick Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span>Recent Payroll Runs</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{payrollRuns.slice(0, 5).length}</div>
                <p className="text-sm text-muted-foreground mt-1">Last 5 payroll runs</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center space-x-2">
                  <Award className="h-4 w-4 text-green-600" />
                  <span>Benefits</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{benefits.length}</div>
                <p className="text-sm text-muted-foreground mt-1">Active benefit plans</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-purple-600" />
                  <span>Time Records</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{timeRecords.length}</div>
                <p className="text-sm text-muted-foreground mt-1">Attendance records</p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Payroll Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollRuns.slice(0, 5).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No recent payroll activity
                      </TableCell>
                    </TableRow>
                  ) : (
                    payrollRuns.slice(0, 5).map((run: any) => (
                      <TableRow key={run.id}>
                        <TableCell>{formatDate(run.pay_date || run.created_at)}</TableCell>
                        <TableCell>{getEmployeeName(run.employee_id || '')}</TableCell>
                        <TableCell>{formatCurrency(run.total_amount || 0)}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusColor(run.status || 'DRAFT')}>
                            {run.status || 'DRAFT'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Runs Tab */}
        <TabsContent value="runs" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              View all payroll runs and their status
            </p>
            <Button onClick={() => navigate('/payroll/runs')}>
              View All Runs
            </Button>
          </div>
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Run ID</TableHead>
                    <TableHead>Pay Period</TableHead>
                    <TableHead>Employees</TableHead>
                    <TableHead>Total Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollRuns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No payroll runs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    payrollRuns.map((run: any) => (
                      <TableRow key={run.id}>
                        <TableCell className="font-mono text-sm">{run.id.slice(0, 8)}</TableCell>
                        <TableCell>
                          {formatDate(run.pay_period_start)} - {formatDate(run.pay_period_end)}
                        </TableCell>
                        <TableCell>{run.employee_count || 1}</TableCell>
                        <TableCell>{formatCurrency(run.total_amount || 0)}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusColor(run.status || 'DRAFT')}>
                            {run.status || 'DRAFT'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employee Profiles Tab */}
        <TabsContent value="profiles" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Manage employee payroll profiles and settings
            </p>
            <Button onClick={() => navigate('/payroll/profiles')}>
              Manage Profiles
            </Button>
          </div>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="font-medium">
                  {payrollProfiles.length} Employee Payroll Profiles
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Click "Manage Profiles" to view and edit employee payroll information
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Salary Components Tab */}
        <TabsContent value="components" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-green-700">Earnings & Benefits</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Active Benefits</span>
                    <Badge>{benefits.length}</Badge>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => navigate('/payroll/benefits')}
                  >
                    Manage Benefits
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-red-700">Deductions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Active Deductions</span>
                    <Badge>{deductions.length}</Badge>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => navigate('/payroll/structures')}
                  >
                    View Salary Structures
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Payroll;
